import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, compose, withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import { connect } from 'react-redux';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import {
  Row,
  Col,
  Panel,
  FormGroup,
  FormControl,
  ControlLabel,
  Button,
  Table,
  Badge
} from 'react-bootstrap';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import moment from 'moment';
import { Star, Crown, Banknote, Target, Settings, DollarSign, BarChart2, BookOpen, FileText, Check, X, AlertTriangle, Save } from 'lucide-react';

import showToaster from '../../../helpers/toasterMessages/showToaster';
import s from './FeaturedSettings.css';

// GraphQL Queries and Mutations
const GET_FEATURED_SETTINGS = gql`
  query getFeaturedSettings {
    getFeaturedSettings {
      monthlyPrice
      premiumMonthlyPrice
      bidPercentage
      currency
    }
  }
`;

const GET_ALL_FEATURED = gql`
  query getAllFeaturedListings {
    getAllFeaturedListings {
      id
      listId
      userId
      status
      monthlyFee
      commissionRate
      tier
      startDate
      endDate
      autoRenew
      listing {
        id
        title
        city
      }
    }
  }
`;

const UPDATE_FEATURED_SETTINGS = gql`
  mutation updateFeaturedSettings($monthlyPrice: Float!, $premiumMonthlyPrice: Float, $bidPercentage: Float!) {
    updateFeaturedSettings(monthlyPrice: $monthlyPrice, premiumMonthlyPrice: $premiumMonthlyPrice, bidPercentage: $bidPercentage) {
      status
      errorMessage
    }
  }
`;

class FeaturedSettings extends Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      monthlyPrice: 99,
      premiumMonthlyPrice: 199,
      bidPercentage: 5,
      isLoading: false,
      initialLoading: true,
      allFeatured: [],
      stats: {
        totalActive: 0,
        totalPremium: 0,
        totalRevenue: 0,
        totalBidPool: 0
      }
    };
  }

  async componentDidMount() {
    await this.loadSettings();
    await this.loadFeaturedListings();
  }

  loadSettings = async () => {
    const { client } = this.props;
    
    try {
      const result = await client.query({
        query: GET_FEATURED_SETTINGS,
        fetchPolicy: 'network-only'
      });
      
      if (result?.data?.getFeaturedSettings) {
        const settings = result.data.getFeaturedSettings;
        this.setState({
          monthlyPrice: settings.monthlyPrice ?? 99,
          premiumMonthlyPrice: settings.premiumMonthlyPrice ?? 199,
          bidPercentage: settings.bidPercentage ?? 5, // Use ?? to allow 0
          initialLoading: false
        });
      }
    } catch (error) {
      console.error('Error loading featured settings:', error);
      this.setState({ initialLoading: false });
    }
  };

  loadFeaturedListings = async () => {
    const { client } = this.props;
    
    try {
      const result = await client.query({
        query: GET_ALL_FEATURED,
        fetchPolicy: 'network-only'
      });
      
      if (result?.data?.getAllFeaturedListings) {
        const all = result.data.getAllFeaturedListings;
        const active = all.filter(f => f.status === 'active');
        const premium = active.filter(f => f.tier === 'premium');
        const totalRevenue = all.reduce((sum, f) => sum + (f.monthlyFee || 0) + (f.bidAmount || 0), 0);
        const totalBidPool = active.reduce((sum, f) => sum + (f.remainingBid || 0), 0);
        
        this.setState({
          allFeatured: all,
          stats: {
            totalActive: active.length,
            totalPremium: premium.length,
            totalRevenue,
            totalBidPool
          }
        });
      }
    } catch (error) {
      console.error('Error loading featured listings:', error);
    }
  };

  handleChange = (field, value) => {
    this.setState({ [field]: parseFloat(value) || 0 });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { client } = this.props;
    const { monthlyPrice, premiumMonthlyPrice, bidPercentage } = this.state;

    this.setState({ isLoading: true });

    try {
      const result = await client.mutate({
        mutation: UPDATE_FEATURED_SETTINGS,
        variables: {
          monthlyPrice: parseFloat(monthlyPrice),
          premiumMonthlyPrice: parseFloat(premiumMonthlyPrice),
          bidPercentage: parseFloat(bidPercentage)
        }
      });

      if (result?.data?.updateFeaturedSettings?.status === 'success') {
        showToaster({ messageId: 'updateSuccess', toasterType: 'success' });
      } else {
        showToaster({ 
          messageId: 'commonError', 
          toasterType: 'error',
          requestMessage: result?.data?.updateFeaturedSettings?.errorMessage
        });
      }
    } catch (error) {
      console.error('Error updating featured settings:', error);
      showToaster({ messageId: 'commonError', toasterType: 'error' });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  getStatusBadge = (status) => {
    const styles = {
      active: { background: '#D1FAE5', color: '#065F46' },
      expired: { background: '#FEE2E2', color: '#991B1B' },
      cancelled: { background: '#F3F4F6', color: '#6B7280' },
      pending: { background: '#FEF3C7', color: '#92400E' }
    };
    return styles[status] || styles.pending;
  };

  render() {
    const { title } = this.props;
    const { monthlyPrice, premiumMonthlyPrice, bidPercentage, isLoading, initialLoading, allFeatured, stats } = this.state;

    if (initialLoading) {
      return (
        <div className={s.container}>
          <h1 className={s.pageTitle}>{title}</h1>
          <p>Cargando...</p>
        </div>
      );
    }

    const activeListings = allFeatured.filter(f => f.status === 'active');

    return (
      <div className={s.container}>
        <h1 className={s.pageTitle}>{title}</h1>
        <p className={s.pageSubtitle}>Configure pricing and manage featured listings monetization</p>
        
        {/* Stats Overview */}
        <div className={s.statsRow}>
          <div className={cx(s.statCard, s.statCardBlue)}>
            <div className={s.statIcon}><Star size={24} /></div>
            <div className={s.statInfo}>
              <span className={s.statNumber}>{stats.totalActive}</span>
              <span className={s.statTitle}>Destacados Activos</span>
            </div>
          </div>
          <div className={cx(s.statCard, s.statCardGold)}>
            <div className={s.statIcon}><Crown size={24} /></div>
            <div className={s.statInfo}>
              <span className={s.statNumber}>{stats.totalPremium}</span>
              <span className={s.statTitle}>Premium Activos</span>
            </div>
          </div>
          <div className={cx(s.statCard, s.statCardGreen)}>
            <div className={s.statIcon}><Banknote size={24} /></div>
            <div className={s.statInfo}>
              <span className={s.statNumber}>${stats.totalRevenue.toFixed(0)}</span>
              <span className={s.statTitle}>Ingresos Totales</span>
            </div>
          </div>
          <div className={cx(s.statCard, s.statCardPurple)}>
            <div className={s.statIcon}><Target size={24} /></div>
            <div className={s.statInfo}>
              <span className={s.statNumber}>${stats.totalBidPool.toFixed(0)}</span>
              <span className={s.statTitle}>Pool de Bids Activo</span>
            </div>
          </div>
        </div>

        {/* Pricing Settings */}
        <Panel className={s.panel}>
          <Panel.Heading>
            <Panel.Title><Settings size={18} /> Configuración de Precios</Panel.Title>
          </Panel.Heading>
          <Panel.Body>
            <form onSubmit={this.handleSubmit}>
              <Row>
                <Col md={4}>
                  <FormGroup>
                    <ControlLabel><DollarSign size={14} /> Precio Standard (USD/mes)</ControlLabel>
                    <FormControl
                      type="number"
                      value={monthlyPrice}
                      onChange={(e) => this.handleChange('monthlyPrice', e.target.value)}
                      min="0"
                      step="0.01"
                      placeholder="99.00"
                      className={s.priceInput}
                    />
                    <p className={s.helpText}>
                      Precio mensual para el plan Standard con badge "Destacado".
                    </p>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <ControlLabel><Crown size={14} /> Precio Premium (USD/mes)</ControlLabel>
                    <FormControl
                      type="number"
                      value={premiumMonthlyPrice}
                      onChange={(e) => this.handleChange('premiumMonthlyPrice', e.target.value)}
                      min="0"
                      step="0.01"
                      placeholder="199.00"
                      className={s.priceInputPremium}
                    />
                    <p className={s.helpText}>
                      Precio mensual para el plan Premium con badge dorado y prioridad.
                    </p>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <ControlLabel><BarChart2 size={14} /> Comisión Máxima (%)</ControlLabel>
                    <FormControl
                      type="number"
                      value={bidPercentage}
                      onChange={(e) => this.handleChange('bidPercentage', e.target.value)}
                      min="0"
                      max="100"
                      step="1"
                      placeholder="40"
                    />
                    <p className={s.helpText}>
                      Máximo % de comisión que un owner puede ofrecer (0-40% recomendado).
                    </p>
                  </FormGroup>
                </Col>
              </Row>

              <div className={s.buttonContainer}>
                <Button
                  type="submit"
                  bsStyle="primary"
                  disabled={isLoading}
                  className={s.submitButton}
                >
                  {isLoading ? 'Guardando...' : <><Save size={16} /> Guardar Configuración</>}
                </Button>
              </div>
            </form>
          </Panel.Body>
        </Panel>

        {/* How it works */}
        <Panel className={s.panel}>
          <Panel.Heading>
            <Panel.Title><BookOpen size={18} /> Cómo Funciona el Sistema</Panel.Title>
          </Panel.Heading>
          <Panel.Body>
            <Row>
              <Col md={6}>
                <div className={s.tierCard}>
                  <div className={s.tierHeader}>
                    <span className={s.tierIcon}><Star size={20} /></span>
                    <span className={s.tierName}>Plan Standard</span>
                    <span className={s.tierPrice}>${monthlyPrice}/mes</span>
                  </div>
                  <ul className={s.tierFeatures}>
                    <li>✓ Aparece en la página principal</li>
                    <li>✓ Badge "Destacado" azul</li>
                    <li>✓ Posicionamiento por comisión</li>
                    <li>✓ Dashboard de estadísticas</li>
                  </ul>
                </div>
              </Col>
              <Col md={6}>
                <div className={cx(s.tierCard, s.tierCardPremium)}>
                  <div className={s.tierHeader}>
                    <span className={s.tierIcon}><Crown size={20} /></span>
                    <span className={s.tierName}>Plan Premium</span>
                    <span className={s.tierPrice}>${premiumMonthlyPrice}/mes</span>
                  </div>
                  <ul className={s.tierFeatures}>
                    <li>✓ Todo lo de Standard</li>
                    <li>✓ Badge "Premium" dorado</li>
                    <li>✓ Prioridad en búsquedas</li>
                    <li>✓ Analytics avanzados</li>
                  </ul>
                </div>
              </Col>
            </Row>
          </Panel.Body>
        </Panel>

        {/* Active Featured Listings Table */}
        <Panel className={s.panel}>
          <Panel.Heading>
            <Panel.Title><FileText size={18} /> Listings Destacados Activos ({activeListings.length})</Panel.Title>
          </Panel.Heading>
          <Panel.Body>
            {activeListings.length === 0 ? (
              <p className={s.emptyMessage}>No hay listings destacados activos en este momento.</p>
            ) : (
              <Table responsive hover className={s.table}>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Bote</th>
                    <th>Tier</th>
                    <th>Cuota</th>
                    <th>Comisión</th>
                    <th>Posición</th>
                    <th>Vence</th>
                    <th>Auto-Renovar</th>
                  </tr>
                </thead>
                <tbody>
                  {activeListings.map((featured, index) => (
                    <tr key={featured.id}>
                      <td>#{featured.id}</td>
                      <td>
                        <a href={`/boats/${featured.listId}`} target="_blank" rel="noopener noreferrer">
                          {featured.listing?.title || `Listing #${featured.listId}`}
                        </a>
                        {featured.listing?.city && <small className={s.cityLabel}> - {featured.listing.city}</small>}
                      </td>
                      <td>
                        <span className={cx(s.tierBadge, featured.tier === 'premium' ? s.tierBadgePremium : s.tierBadgeStandard)}>
                          {featured.tier === 'premium' ? <><Crown size={14} /> Premium</> : <><Star size={14} /> Standard</>}
                        </span>
                      </td>
                      <td>${(featured.monthlyFee || 0).toFixed(2)}</td>
                      <td style={{ fontWeight: '600', color: '#D97706' }}>
                        {(featured.commissionRate || 0).toFixed(1)}%
                      </td>
                      <td>
                        <span style={{ 
                          background: index === 0 ? '#22C55E' : '#E5E7EB', 
                          color: index === 0 ? 'white' : '#374151',
                          padding: '4px 10px', 
                          borderRadius: '12px', 
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          #{index + 1}
                        </span>
                      </td>
                      <td>{featured.endDate ? moment(featured.endDate).format('DD/MM/YYYY') : '-'}</td>
                      <td>
                        {featured.autoRenew ? (
                          <span className={s.autoRenewOn}><Check size={14} /> Sí</span>
                        ) : (
                          <span className={s.autoRenewOff}><X size={14} /> No</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Panel.Body>
        </Panel>
      </div>
    );
  }
}

const mapState = (state) => ({});

export default compose(
  withStyles(s),
  withApollo,
  connect(mapState),
)(FeaturedSettings);
